/* global gdAlertify */
import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import EnvioMensajeModal from "./EnvioMensajeModal";
import { BadgeEstado, BarraAvance, ESTADOS_LOTE_ABIERTOS, ESTADOS_MENSAJE, IconoCanal, ORIGENES, fechaHora, mensajeError } from "./enviosUi";

const POR_PAGINA = 50;

const EnvioLoteDetalle = ({ loteId, onVolver }) => {
    const [lote, setLote] = useState(null);
    const [mensajes, setMensajes] = useState({ items: [], total: 0 });
    const [filtros, setFiltros] = useState({ estado: "", canal: "", q: "" });
    const [pagina, setPagina] = useState(1);
    const [loading, setLoading] = useState(true);
    const [mensajeId, setMensajeId] = useState(null);

    const cargar = useCallback(async (silencioso = false) => {
        if (!silencioso) setLoading(true);
        try {
            const { data } = await axiosInstance.get(`/envios/lotes/${loteId}`, {
                params: { ...filtros, page: pagina, limit: POR_PAGINA },
            });
            setLote(data?.data?.lote || null);
            setMensajes(data?.data?.mensajes || { items: [], total: 0 });
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudo cargar el envío."));
        } finally {
            setLoading(false);
        }
    }, [loteId, filtros, pagina]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    // Lote en curso: refresco cada 15 s (los estados de WhatsApp llegan por webhook).
    const abierto = lote && ESTADOS_LOTE_ABIERTOS.includes(lote.estado);
    useEffect(() => {
        if (!abierto) return undefined;
        const intervalo = setInterval(() => cargar(true), 15000);
        return () => clearInterval(intervalo);
    }, [abierto, cargar]);

    const accion = (titulo, mensaje, ruta, exito) => {
        gdAlertify.confirm({
            title: titulo,
            message: mensaje,
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    const { data } = await axiosInstance.post(ruta);
                    gdAlertify.notiSuccess(exito(data?.data || {}));
                    cargar(true);
                } catch (error) {
                    gdAlertify.notiError(mensajeError(error, "No se pudo completar la acción."));
                } finally {
                    gdAlertify.hideLoader();
                }
            },
        });
    };

    const cambiarFiltro = (campo, valor) => {
        setPagina(1);
        setFiltros((prev) => ({ ...prev, [campo]: valor }));
    };

    if (loading && !lote) {
        return <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>;
    }
    if (!lote) {
        return (
            <button className="btn btn-outline-secondary" onClick={onVolver}>
                <i className="bi bi-arrow-left me-1"></i>Volver
            </button>
        );
    }

    const paginas = Math.max(1, Math.ceil(mensajes.total / POR_PAGINA));
    const contadores = [
        ["Total", lote.total, "dark"],
        ["Pendientes", lote.pendientes, "secondary"],
        ["Enviados", lote.enviados, "primary"],
        ["Entregados", lote.entregados, "success"],
        ["Leídos", lote.leidos, "success"],
        ["Fallidos", lote.fallidos, "danger"],
        ["Cancelados", lote.cancelados, "dark"],
    ];

    return (
        <>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <button className="btn btn-sm btn-outline-secondary me-2" onClick={onVolver}>
                        <i className="bi bi-arrow-left me-1"></i>Volver
                    </button>
                    <h5 className="d-inline align-middle mb-0">
                        #{lote.id} · {lote.nombre}
                    </h5>
                    <span className="ms-2 align-middle"><BadgeEstado estado={lote.estado} tipo="lote" /></span>
                    <div className="text-muted small mt-1">
                        {ORIGENES[lote.origen] || lote.origen}
                        {lote.referencia && <> · {lote.referencia}</>}
                        {" · "}creado {fechaHora(lote.created_at)}
                        {lote.iniciado_at && <> · inició {fechaHora(lote.iniciado_at)}</>}
                        {lote.finalizado_at && <> · terminó {fechaHora(lote.finalizado_at)}</>}
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => cargar()}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Actualizar
                    </button>
                    {Number(lote.fallidos) > 0 && (
                        <button className="btn btn-sm btn-outline-warning"
                            onClick={() => accion("Reintentar fallidos", `¿Volver a encolar los ${lote.fallidos} mensajes fallidos?`,
                                `/envios/lotes/${lote.id}/reintentar`, (d) => `${d.reencolados} mensajes reencolados.`)}>
                            <i className="bi bi-arrow-counterclockwise me-1"></i>Reintentar fallidos
                        </button>
                    )}
                    {Number(lote.pendientes) > 0 && (
                        <button className="btn btn-sm btn-outline-danger"
                            onClick={() => accion("Cancelar pendientes", "Los mensajes que aún no salen se cancelarán. ¿Continuar?",
                                `/envios/lotes/${lote.id}/cancelar`, (d) => `${d.cancelados} mensajes cancelados.`)}>
                            <i className="bi bi-x-circle me-1"></i>Cancelar pendientes
                        </button>
                    )}
                </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-2">
                {contadores.map(([label, valor, color]) => (
                    <div key={label} className="border rounded px-3 py-2 text-center" style={{ minWidth: 100 }}>
                        <div className={`fs-5 fw-semibold text-${color}`}>{valor}</div>
                        <small className="text-muted">{label}</small>
                    </div>
                ))}
            </div>
            <div className="mb-3"><BarraAvance lote={lote} /></div>

            <div className="row g-2 mb-2">
                <div className="col-md-3">
                    <select className="form-select form-select-sm" value={filtros.estado} onChange={(e) => cambiarFiltro("estado", e.target.value)}>
                        <option value="">Todos los estados</option>
                        {Object.entries(ESTADOS_MENSAJE).map(([valor, def]) => <option key={valor} value={valor}>{def.label}</option>)}
                    </select>
                </div>
                <div className="col-md-3">
                    <select className="form-select form-select-sm" value={filtros.canal} onChange={(e) => cambiarFiltro("canal", e.target.value)}>
                        <option value="">Correo y WhatsApp</option>
                        <option value="email">Correo</option>
                        <option value="whatsapp">WhatsApp</option>
                    </select>
                </div>
                <div className="col-md-6">
                    <input className="form-control form-control-sm" placeholder="Buscar destinatario o asunto" value={filtros.q}
                        onChange={(e) => cambiarFiltro("q", e.target.value)} />
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-sm table-hover align-middle">
                    <thead className="table-light">
                        <tr>
                            <th></th>
                            <th>Destinatario</th>
                            <th>Contenido</th>
                            <th>Estado</th>
                            <th className="text-center">Intentos</th>
                            <th>Enviado</th>
                            <th>Detalle</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mensajes.items.map((m) => (
                            <tr key={m.id} role="button" onClick={() => setMensajeId(m.id)}>
                                <td><IconoCanal tipo={m.canal_tipo} /></td>
                                <td>
                                    {m.destinatario}
                                    {m.destinatario_nombre && <small className="d-block text-muted">{m.destinatario_nombre}</small>}
                                </td>
                                <td><small>{m.asunto || (m.plantilla ? `Plantilla: ${m.plantilla}` : "Texto libre")}</small></td>
                                <td><BadgeEstado estado={m.estado} /></td>
                                <td className="text-center">{m.intentos}</td>
                                <td><small>{fechaHora(m.leido_at || m.entregado_at || m.enviado_at)}</small></td>
                                <td className="text-truncate" style={{ maxWidth: 280 }}>
                                    <small className={m.estado === "fallido" ? "text-danger" : "text-muted"} title={m.error_detalle || ""}>
                                        {m.error_detalle || (m.estado === "error" ? `Reintento ${fechaHora(m.proximo_intento_at)}` : "")}
                                    </small>
                                </td>
                            </tr>
                        ))}
                        {mensajes.items.length === 0 && (
                            <tr><td colSpan="7" className="text-center text-muted py-3">Sin mensajes para este filtro.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {paginas > 1 && (
                <div className="d-flex justify-content-end align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
                        <i className="bi bi-chevron-left"></i>
                    </button>
                    <small>Página {pagina} de {paginas}</small>
                    <button className="btn btn-sm btn-outline-secondary" disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>
                        <i className="bi bi-chevron-right"></i>
                    </button>
                </div>
            )}

            {mensajeId && (
                <EnvioMensajeModal mensajeId={mensajeId} onClose={() => setMensajeId(null)} onCambio={() => cargar(true)} />
            )}
        </>
    );
};

export default EnvioLoteDetalle;
